/* import __COLOCATED_TEMPLATE__ from './add-mcp-actions-modal.hbs'; */
/* eslint-disable @intercom/intercom/no-bare-strings */
/* RESPONSIBLE TEAM: team-ml */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { inject as service } from '@ember/service';
import { taskFor } from 'ember-concurrency-ts';
import { get, post } from 'embercom/lib/ajax';
import { type TaskGenerator } from 'ember-concurrency';
import { getListIconForMcpServer } from '../../models/workflow-connector/constants/mcp-templates';
import type McpServer from 'embercom/models/workflow-connector/mcp-server';
import { action } from '@ember/object';

interface Args {
  onModalClose: () => void;
  mcpServer: McpServer | null;
}

class SelectableTool {
  @tracked isSelected = false;
  tool: any;

  constructor(tool: any) {
    this.tool = tool;
  }
}

export default class AddMcpActionsModal extends Component<Args> {
  @service declare store: $TSFixMe;
  @service declare appService: $TSFixMe;
  @service declare router: $TSFixMe;
  @service declare intercomEventService: any;

  @tracked selectableTools: SelectableTool[] = [];
  @tracked selectAllState = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.fetchMcpServerTools).perform();
  }

  get serverIcon(): string {
    return getListIconForMcpServer(this.args.mcpServer?.name ?? '');
  }

  get canSubmit(): boolean {
    return this.selectedTools.length > 0;
  }

  get areAllToolsSelected(): boolean {
    return this.selectableTools.every((tool) => tool.isSelected) && this.selectableTools.length > 0;
  }

  get selectedTools() {
    return this.selectableTools
      .filter((tool) => tool.isSelected)
      .map((selectableTool) => selectableTool.tool);
  }

  getToolDisplayName(toolName: string): string {
    return toolName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  @action
  toggleToolSelection(tool: SelectableTool) {
    tool.isSelected = !tool.isSelected;
    this.selectAllState = this.areAllToolsSelected;
  }

  @action
  onToggleSelectAll() {
    let newState = !this.areAllToolsSelected;
    this.selectAllState = newState;
    this.selectableTools.forEach((tool) => {
      tool.isSelected = newState;
    });
    this.selectableTools = [...this.selectableTools];
  }

  trackAction(actionName: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: actionName,
      object: 'mcp_action',
    });
  }

  @task({ drop: true })
  *fetchMcpServerTools(): TaskGenerator<void> {
    try {
      let response = yield get(
        '/ember/workflow_connector/setup_mcp_actions/list_mcp_server_tools',
        {
          app_id: this.appService.app.id,
          mcp_server_id: this.args.mcpServer?.id,
        },
      );
      this.selectableTools = response.tools.map((tool: any) => new SelectableTool(tool));
    } catch (error) {
      console.error('Error fetching MCP server tools:', error);
    }
  }

  @task({ drop: true })
  *createMcpActions(): TaskGenerator<void> {
    this.trackAction('mcp_action_creation_attempt');

    try {
      yield post('/ember/workflow_connector/setup_mcp_actions/create_mcp_actions', {
        app_id: this.appService.app.id,
        mcp_server_id: this.args.mcpServer?.id,
        tools: this.selectedTools,
      });

      this.trackAction('mcp_action_creation_success');

      yield this.store.findAll('workflow-connector/setup-action', { reload: true });

      this.args.onModalClose();
    } catch (error) {
      console.error('Error adding MCP server:', error);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'WorkflowConnector::AddMcpActionsModal': typeof AddMcpActionsModal;
    'workflow-connector/add-mcp-actions-modal': typeof AddMcpActionsModal;
  }
}
